/* One-time "set up your phone" walkthrough for drivers — so GPS never goes dark. */
import { useState } from "react";
import { Smartphone, Share, MoreVertical, MapPin, BatteryCharging, CheckCircle2, X } from "lucide-react";

const KEY = "armorbatch.phoneSetupDone";
export const phoneSetupDone = () => localStorage.getItem(KEY) === "1";
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

export function PhoneSetupGuide({ onDone }) {
  const [step, setStep] = useState(0);
  const ios = isIOS();
  const steps = [
    {
      icon: Smartphone, title: "Add this app to your home screen",
      body: ios
        ? <>Tap the <Share className="inline w-4 h-4 mx-0.5" /> <b>Share</b> button at the bottom of Safari, scroll down and tap <b>Add to Home Screen</b>, then <b>Add</b>. Open the app from the new icon from now on — not from Safari.</>
        : <>Tap the <MoreVertical className="inline w-4 h-4 mx-0.5" /> menu (top right of Chrome), then <b>Add to Home screen</b> / <b>Install app</b>. Open it from the new icon from now on.</>,
      done: isStandalone() ? "Already installed — nice." : null,
    },
    {
      icon: MapPin, title: "Allow Location — Always / While Using",
      body: ios
        ? <>When the app asks, tap <b>Allow While Using App</b>. Then in <b>Settings → Privacy & Security → Location Services → Safari Websites</b> choose <b>While Using the App</b> and switch on <b>Precise Location</b>.</>
        : <>When the app asks, tap <b>While using the app</b>. Then in <b>Settings → Apps → Chrome → Permissions → Location</b> choose <b>Allow all the time</b> and switch on <b>Use precise location</b>.</>,
    },
    {
      icon: BatteryCharging, title: "Stop the phone killing the app",
      body: ios
        ? <>Turn <b>Low Power Mode</b> off while you're on a load, keep the phone on the truck charger, and leave the Driver App open on screen — it keeps the screen awake for you.</>
        : <>In <b>Settings → Battery</b> turn off <b>Battery Saver</b>, and set the Driver App / Chrome to <b>Unrestricted</b> under Battery. Keep the phone on the truck charger and the app open on screen.</>,
    },
    {
      icon: CheckCircle2, title: "You're set",
      body: <>The green <b>GPS LIVE</b> bar at the top means dispatch can see you. If it ever turns red, tap it. If you ignore it, you'll get a text — and so will the office. No excuses now.</>,
    },
  ];
  const s = steps[step];
  const Icon = s.icon;
  const finish = () => { localStorage.setItem(KEY, "1"); onDone?.(); };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center" data-testid="phone-setup-guide">
      <div className="bg-white text-[#0f1419] w-full max-w-md p-5 pb-8 sm:pb-5 border-t-4 border-[rgb(var(--primary))]">
        <div className="flex items-center justify-between">
          <div className="mono text-[10px] uppercase tracking-widest text-[rgb(var(--primary))] font-bold">Set up your phone · {step + 1} / {steps.length}</div>
          <button type="button" onClick={finish} data-testid="phone-setup-skip" className="text-gray-400 p-1" aria-label="Skip"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1 mt-2">{steps.map((_, i) => <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-[rgb(var(--primary))]" : "bg-gray-200"}`} />)}</div>
        <div className="mt-5 flex items-start gap-3">
          <div className="w-12 h-12 shrink-0 bg-[rgb(var(--primary))]/10 flex items-center justify-center"><Icon className="w-6 h-6 text-[rgb(var(--primary))]" /></div>
          <div>
            <div className="heading text-xl font-black leading-tight" data-testid="phone-setup-title">{s.title}</div>
            {s.done && <div className="mono text-[11px] text-emerald-600 font-bold mt-1">{s.done}</div>}
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">{s.body}</p>
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          {step > 0 && <button type="button" onClick={() => setStep(step - 1)} className="flex-1 h-14 border-2 border-gray-300 font-bold uppercase mono text-xs">Back</button>}
          {step < steps.length - 1
            ? <button type="button" onClick={() => setStep(step + 1)} data-testid="phone-setup-next" className="flex-[2] h-14 bg-[#0f1419] text-white font-black uppercase mono text-sm">Done — next</button>
            : <button type="button" onClick={finish} data-testid="phone-setup-finish" className="flex-[2] h-14 bg-[rgb(var(--primary))] text-black font-black uppercase mono text-sm">All set</button>}
        </div>
      </div>
    </div>
  );
}
